import React, { useMemo, useState } from 'react';
import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { useTranslation } from 'react-i18next';
import { logger } from '../../lib/logger';
import type { Incident } from '../../types';

interface IncidentMarkerProps {
  incident: Incident;
  isHovered: boolean;
  onClick: (slug: string) => void;
  onMouseOver: () => void;
  onMouseOut: () => void;
  index: number;
  totalCount: number;
}

export default function IncidentMarker({
  incident,
  isHovered,
  onClick,
  onMouseOver,
  onMouseOut,
  index,
  totalCount
}: IncidentMarkerProps) {
  const { t } = useTranslation();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  
  // Create marker position from coordinates with slight offset if markers are close
  const position = useMemo(() => {
    if (!incident.location?.coordinates) {
      logger.warn('Missing coordinates for incident', 'IncidentMarker', { id: incident.id });
      return null;
    }

    const [lng, lat] = incident.location.coordinates;
    
    // If we have multiple incidents in the same area (determined by totalCount)
    // create a small offset based on the index to spread them out in a circular pattern
    let adjustedLat = lat;
    let adjustedLng = lng;
    
    if (totalCount > 1) {
      // Create a small offset (0.0001 degrees is roughly 10 meters)
      // Use a circular pattern for spreading
      const offsetDistance = 0.0002; // Adjust this value to control spread distance
      const angle = (index / totalCount) * 2 * Math.PI;
      
      adjustedLat = lat + offsetDistance * Math.sin(angle);
      adjustedLng = lng + offsetDistance * Math.cos(angle);
    }
    
    // Add debug logging
    logger.info('Creating marker position', 'IncidentMarker', {
      id: incident.id,
      original: [lat, lng],
      adjusted: [adjustedLat, adjustedLng],
      index,
      totalCount
    });

    return [adjustedLat, adjustedLng] as [number, number];
  }, [incident.location, index, totalCount]);

  // Get incident emoji based on type
  const getIncidentEmoji = (type: string) => {
    switch (type) {
      case 'GANG_ACTIVITY': return '🥷🏾';
      case 'SEXUAL_VIOLENCE': return '👩🏾‍🦰';
      case 'CIVIL_UNREST': return '🔥';
      case 'KIDNAPPING': return '🚨';
      case 'ROBBERY': return '💰';
      case 'NATURAL_DISASTER': return '🌪️';
      case 'ROAD_CLOSURE': return '🚧';
      default: return '⚠️';
    }
  };

  // Create a custom div icon with the emoji
  const icon = useMemo(() => {
    const emoji = getIncidentEmoji(incident.type);
    const size = isHovered ? 40 : 32;
    
    return L.divIcon({
      html: `<div class="emoji-icon ${isHovered ? 'hovered' : ''}" style="font-size: ${size}px;">${emoji}</div>`,
      className: 'emoji-marker',
      iconSize: [size, size],
      iconAnchor: [size/2, size/2]
    });
  }, [incident.type, isHovered]);

  // Don't render if we don't have valid position
  if (!position) {
    return null;
  }

  const getSeverityColor = (severity: string) => {
    const colors = {
      CRITICAL: 'bg-red-100 text-red-700',
      HIGH: 'bg-orange-100 text-orange-700',
      MODERATE: 'bg-yellow-100 text-yellow-700',
      LOW: 'bg-blue-100 text-blue-700'
    };
    return colors[severity as keyof typeof colors] || 'bg-gray-100 text-gray-700';
  };

  return (
    <Marker
      position={position}
      icon={icon}
      eventHandlers={{
        click: () => onClick(incident.slug),
        mouseover: onMouseOver,
        mouseout: onMouseOut,
      }}
      zIndexOffset={isHovered ? 1000 : index}
    >
      <Popup
        onOpen={() => setIsPopupOpen(true)}
        onClose={() => setIsPopupOpen(false)}
      >
        <div className="p-2">
          <h3 className="font-semibold mb-1">
            {t(`incident.types.${incident.type}`)}
          </h3>
          <div className="text-sm text-gray-600">
            {incident.location_zone}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {position[0].toFixed(6)}, {position[1].toFixed(6)}
          </div>
        </div>
      </Popup>
    </Marker>
  );
}